import { announce } from '@theme/component';
import { formatItemCount } from '@theme/cart-math';

const filterOriginParam = 'filter_origin';
const defaultFilterOrigin = '/products';
const filterAttributeByParam = new Map([
  ['filter.p.m.custom.filter_categories', 'filterCategoryValues'],
  ['filter.p.m.custom.filter_concerns', 'filterConcernValues'],
  ['filter.p.m.custom.filter_key_ingredients', 'filterIngredientValues'],
  ['filter.p.m.custom.filter_skin_types', 'filterSkinTypeValues'],
]);

const normalizedFilterOperator = (operator) => operator === 'and' ? 'and' : 'or';
const filterInputKey = (input) => `${input.name}\u0000${input.value}`;

export function orderedFilterValueKeys(currentKeys, sourceKeys) {
  const currentSet = new Set(currentKeys);
  const orderedKeys = [];
  const orderedSet = new Set();

  sourceKeys.forEach((key) => {
    if (!currentSet.has(key) || orderedSet.has(key)) return;
    orderedKeys.push(key);
    orderedSet.add(key);
  });
  currentKeys.forEach((key) => {
    if (orderedSet.has(key)) return;
    orderedKeys.push(key);
  });
  return orderedKeys;
}

function filterGroupsMatchValues(values, groups) {
  return Array.from(groups.entries()).every(([paramName, group]) => {
    const cardValues = values.get(paramName) || new Set();
    const selectedValues = Array.from(group.values);
    if (normalizedFilterOperator(group.operator) === 'and') {
      return selectedValues.every((value) => cardValues.has(value));
    }
    return selectedValues.some((value) => cardValues.has(value));
  });
}

export function filterGroupsMatchCard(card, groups) {
  if (card.members?.length) {
    return card.members.some(
      (member) => member.listed !== false && filterGroupsMatchValues(member.values, groups)
    );
  }
  return filterGroupsMatchValues(card.values, groups);
}

/**
 * Chooses the member a combined card shows for the selected filters.
 * @param {{members?: Array<{productId: string, listed?: boolean, values: Map<string, Set<string>>}>, defaultProductId?: string}} card
 * @param {Map<string, {operator: string, values: Set<string>}>} groups
 * @param {{currentIndex: number, autoSelected: boolean}} state
 * @returns {number} Member index to show, or -1 to keep the current choice.
 */
export function preferredCardMember(card, groups, { currentIndex, autoSelected }) {
  const members = card.members || [];
  if (members.length < 2) return -1;

  const defaultIndex = Math.max(
    0,
    members.findIndex((member) => member.productId === card.defaultProductId)
  );
  if (!groups.size) return autoSelected ? defaultIndex : -1;

  const matching = members.flatMap((member, index) =>
    member.listed !== false && filterGroupsMatchValues(member.values, groups) ? [index] : []
  );
  if (!matching.length) return -1;
  if (matching.length === 1) return matching[0];
  if (!autoSelected && matching.includes(currentIndex)) return -1;
  return matching.includes(defaultIndex) ? defaultIndex : matching[0];
}

export function withCandidateFilterValue(groups, paramName, value, operator = 'or') {
  const candidateGroups = new Map(
    Array.from(groups.entries()).map(([groupParamName, group]) => [
      groupParamName,
      {
        operator: normalizedFilterOperator(group.operator),
        values: new Set(group.values),
      },
    ])
  );
  const currentGroup = candidateGroups.get(paramName) || {
    operator: normalizedFilterOperator(operator),
    values: new Set(),
  };
  currentGroup.values.add(value);
  candidateGroups.set(paramName, currentGroup);
  return candidateGroups;
}

class CollectionFilterDrawer extends HTMLElement {
  async connectedCallback() {
    await customElements.whenDefined('collection-component');
    if (!this.isConnected) return;

    this.abortController?.abort();
    this.abortController = new AbortController();
    this.collection = this.closest('collection-component');
    this.dialog = this.querySelector('dialog');
    this.trigger = this.collection?.querySelector('[data-collection-filter]');
    this.results = this.collection?.querySelector('[data-collection-ref="results"]');
    if (!this.collection || !this.dialog || !this.trigger) return;

    const { signal } = this.abortController;
    this.refreshInstantIndex();
    this.preloadFilterIndex({
      collectionTemplate: true,
      priority: 'high',
    });
    this.refreshTrigger();
    this.markCommitted();
    this.refreshFilterCounts();
    this.applyUrlFiltersOnLoad();
    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('change', this.onChange, { signal });
    this.addEventListener('submit', this.onSubmit, { signal });
    this.addEventListener('reset', this.onReset, { signal });
    this.dialog.addEventListener('click', this.onDialogClick, { signal });
    this.dialog.addEventListener('keydown', this.onDialogKeyDown, { signal });
    this.dialog.addEventListener('cancel', this.onCancel, { signal });
    this.dialog.addEventListener('close', this.onClose, { signal });
    this.collection.addEventListener('product-card:change', this.onCardChange, { signal });
  }

  onCardChange = (event) => {
    event.target.closest?.('[data-collection-card]')?.removeAttribute('data-filter-member-selected');
  };

  get filtersInPlace() {
    return this.dataset.filterScope === 'current';
  }

  markCommitted(form = this.querySelector('[data-collection-ref="filter-form"]')) {
    this.committedSelection = form ? this.selectionKeys(form) : new Set();
  }

  markCommittedFromUrl(url) {
    const destination = new URL(url, window.location.href);
    const keys = new Set();

    Array.from(filterAttributeByParam.keys()).forEach((paramName) => {
      destination.searchParams.getAll(paramName).forEach((value) => {
        if (value) keys.add(filterInputKey({ name: paramName, value }));
      });
    });
    this.committedSelection = keys;
  }

  selectionKeys(form) {
    return new Set(
      Array.from(form.querySelectorAll('input[type="checkbox"]:checked'))
        .filter((input) => filterAttributeByParam.has(input.name))
        .map(filterInputKey)
    );
  }

  draftMatchesCommitted(form) {
    const draft = this.selectionKeys(form);
    const committed = this.committedSelection || new Set();
    return (
      draft.size === committed.size &&
      Array.from(draft).every((key) => committed.has(key))
    );
  }

  restoreCommittedSelection() {
    const form = this.querySelector('[data-collection-ref="filter-form"]');
    if (!form) return;

    const committed = this.committedSelection || new Set();
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = committed.has(filterInputKey(input));
    });
    this.updateDraftState(form);
  }

  targetsCurrentPath(form) {
    if (!form) return false;

    return (
      new URL(form.action, window.location.href).pathname === window.location.pathname
    );
  }

  disconnectedCallback() {
    this.cancelInstantBootstrap();
    this.abortController?.abort();
    this.triggerController?.abort();
    this.close({ restoreFocus: false });
    this.finalizeClose();
  }

  refreshTrigger() {
    this.triggerController?.abort();
    this.triggerController = new AbortController();
    this.trigger = this.collection?.querySelector('[data-collection-filter]');
    this.clearButton = this.collection?.querySelector('[data-collection-filter-clear]');
    if (!this.trigger) return;

    this.trigger.disabled = false;
    this.trigger.removeAttribute('aria-disabled');
    this.trigger.setAttribute('aria-expanded', String(Boolean(this.dialog?.open)));
    this.trigger.addEventListener('click', () => this.open(), {
      signal: this.triggerController.signal,
    });
    if (this.clearButton) {
      this.clearButton.disabled = false;
      this.clearButton.removeAttribute('aria-disabled');
      this.clearButton.addEventListener('click', this.onClear, {
        signal: this.triggerController.signal,
      });
    }
    if (this.dialog?.open) this.activeTrigger = this.trigger;
  }

  applyUrlFiltersOnLoad() {
    if (!this.filtersInPlace) return;

    const form = this.querySelector('[data-collection-ref="filter-form"]');
    if (!form || this.targetsCurrentPath(form)) return;

    const requested = new URL(window.location.href);
    const hasActiveFilters = Array.from(requested.searchParams.entries()).some(
      ([key, value]) => filterAttributeByParam.has(key) && value
    );
    if (!hasActiveFilters) return;

    this.syncFormFromUrl(form, requested.href);
    this.markCommitted(form);
    this.refreshFilterCounts();
    this.applyFilterIntent(form, false, { historyMode: 'replace', announceResults: false });
  }

  async open() {
    if (this.dialog.open || this.openPending) return;
    this.openPending = true;
    this.activeTrigger = this.trigger;
    this.restoreCommittedSelection();
    await this.preloadFilterIndex({
      collectionTemplate: true,
      priority: 'high',
    }).catch(() => null);
    this.openPending = false;
    if (!this.isConnected || this.dialog.open) return;

    this.updateDraftState();
    this.dialog.showModal();
    globalThis.DieuxDialog?.scrollToTop(this.dialog);
    document.documentElement.setAttribute('data-collection-filter-open', 'true');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.querySelector('[data-collection-filter-close]')?.focus({ preventScroll: true });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.dialog?.open) return;

    this.restoreFocusOnClose = restoreFocus;
    this.dialog.close();
  }

  onClick = (event) => {
    if (event.target.closest('[data-collection-filter-close]')) {
      event.preventDefault();
      this.close();
    }
  };

  onChange = (event) => {
    if (!event.target.matches('[data-collection-ref="filter-form"] input[type="checkbox"]')) return;
    this.announceDraft(this.updateDraftState(event.target.form));
  };

  updateDraftState(form = this.querySelector('[data-collection-ref="filter-form"]')) {
    if (!form) return null;

    const selectedGroups = this.selectedGroups(form);
    this.updateDraftCounts(selectedGroups);
    this.updateDraftAvailability(form, selectedGroups);
    return selectedGroups;
  }

  updateDraftAvailability(form, selectedGroups = this.selectedGroups(form)) {
    const cards = this.availabilityCards();
    if (!cards.length) {
      if (!selectedGroups.size) this.resetOptionAvailability(form);
      return false;
    }

    this.updateOptionAvailability(form, selectedGroups, cards);
    return true;
  }

  availabilityCards() {
    if (this.supportsInstantFilters()) return this.instantCards;
    return this.previewCards || [];
  }

  applyFilterIntent(
    form,
    restoreFocus,
    { historyMode = 'push', landOnResults = false, announceResults = true } = {}
  ) {
    const hasActiveFilters = form.querySelector('input[type="checkbox"]:checked');
    if (!hasActiveFilters) {
      this.cancelInstantBootstrap({ cancelNavigation: true });
      this.resetOptionAvailability(form);
      if (this.filtersInPlace) {
        this.clearFiltersInPlace(form, restoreFocus, { landOnResults });
        return;
      }

      if (this.isAtFilterOrigin()) {
        this.restoreFilterFocus(restoreFocus);
        this.announceVisibleResults(0);
        return;
      }

      this.navigateToFilterOrigin({ keepOpen: true, restoreFocus, landOnResults });
      return;
    }

    if (this.supportsInstantFilters()) {
      this.applyInstantFilters(form, { restoreFocus, historyMode, landOnResults, announceResults });
      return;
    }

    if (
      this.dataset.filterBehavior === 'instant' &&
      this.hasAttribute('data-instant-filter-eligible')
    ) {
      this.queueInstantBootstrap(form, restoreFocus, historyMode, landOnResults, announceResults);
      return;
    }

    this.navigate(this.filterUrl(form), {
      keepOpen: true,
      restoreFocus,
      historyMode,
      landOnResults,
      announceResults,
    });
  }

  /**
   * Whether an element is a filter option checkbox inside this drawer's form.
   * @param {EventTarget | null} element
   * @returns {boolean}
   */
  isFilterCheckbox(element) {
    return Boolean(element?.matches?.('[data-collection-ref="filter-form"] input[type="checkbox"]'));
  }

  onDialogClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  onDialogKeyDown = (event) => {
    if (event.key === 'Enter' && !event.isComposing && this.isFilterCheckbox(event.target)) {
      event.preventDefault();
      if (!event.target.disabled) event.target.click();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = Array.from(
      this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.getClientRects().length > 0);
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    if (!first || !last) {
      event.preventDefault();
      this.dialog.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  onClose = () => {
    this.finalizeClose();
  };

  finalizeClose() {
    document.documentElement.removeAttribute('data-collection-filter-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    if (this.committingDraft) {
      this.committingDraft = false;
    } else if (this.isConnected) {
      this.restoreCommittedSelection();
    }
    if (this.restoreFocusOnClose && this.activeTrigger?.isConnected) {
      this.activeTrigger.focus({ preventScroll: true });
    }
    this.activeTrigger = null;
    this.restoreFocusOnClose = true;
  }

  onReset = (event) => {
    event.preventDefault();
    const form = this.querySelector('[data-collection-ref="filter-form"]');
    if (!form) return;

    this.querySelector('[data-collection-filter-close]')?.focus({ preventScroll: true });
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = false;
    });
    this.announceDraft(this.updateDraftState(form));
  };

  onClear = () => {
    const form = this.querySelector('[data-collection-ref="filter-form"]');
    if (!form) return;

    this.restoreFilterFocus('filter');
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = false;
    });
    this.markCommitted(form);
    this.refreshFilterCounts();
    this.cancelInstantBootstrap({ cancelNavigation: true });
    this.resetOptionAvailability(form);
    if (this.filtersInPlace) {
      this.clearFiltersInPlace(form, 'filter', { landOnResults: true });
      return;
    }

    if (this.isAtFilterOrigin()) {
      this.restoreFilterFocus('filter');
      this.announceVisibleResults(0);
      return;
    }

    this.navigateToFilterOrigin({ keepOpen: false, restoreFocus: 'filter', landOnResults: true });
  };

  clearFiltersInPlace(form, restoreFocus, { landOnResults = false } = {}) {
    const url = this.committedUrl(form).href;
    if (
      this.targetsCurrentPath(form) &&
      this.applyInstantFilters(form, { url, restoreFocus, landOnResults })
    ) {
      return;
    }

    if (new URL(url).href === new URL(window.location.href).href) {
      this.restoreFilterFocus(restoreFocus);
      this.announceVisibleResults(0);
      return;
    }

    this.navigate(url, { keepOpen: true, restoreFocus, landOnResults });
  }

  navigateToFilterOrigin({ keepOpen, restoreFocus, landOnResults = false }) {
    const origin = this.filterOrigin();
    const originRoute = this.filterOriginRoute(origin);

    this.navigate(origin.href, {
      keepOpen,
      restoreFocus,
      landOnResults,
      routeKey: originRoute.routeKey,
      targetView: originRoute.targetView,
      title: originRoute.title,
    });
  }

  onSubmit = (event) => {
    const form = event.target.closest('[data-collection-ref="filter-form"]');
    if (!form) return;
    event.preventDefault();

    const unchanged = this.draftMatchesCommitted(form);
    this.committingDraft = Boolean(this.dialog?.open);
    this.close();
    if (unchanged) return;

    this.markCommitted(form);
    this.applyFilterIntent(form, 'filter', { landOnResults: true });
  };

  filterUrl(form) {
    const url = this.committedUrl(form);
    new FormData(form).forEach((value, key) => {
      url.searchParams.append(key, value);
    });
    return url.href;
  }

  targetUrl(form) {
    const url = new URL(form.action, window.location.href);
    this.stripResultParams(url);
    if (!this.filtersInPlace) {
      url.searchParams.set(filterOriginParam, this.filterOrigin().pathname);
    }
    return url;
  }

  committedUrl(form) {
    if (!this.filtersInPlace) return this.targetUrl(form);

    const url = new URL(window.location.pathname, window.location.origin);
    this.stripResultParams(url);
    return url;
  }

  stripResultParams(url) {
    url.search = window.location.search;
    Array.from(url.searchParams.keys()).forEach((key) => {
      if (
        key.startsWith('filter.') ||
        key === filterOriginParam ||
        ['page', 'sort', 'sort_by', 'order', 'in_stock'].includes(key)
      ) {
        url.searchParams.delete(key);
      }
    });
  }

  preloadFilterIndex({ collectionTemplate = false, priority = 'low' } = {}) {
    const form = this.querySelector('[data-collection-ref="filter-form"]');
    if (
      !form ||
      this.dataset.filterBehavior !== 'instant' ||
      !this.hasAttribute('data-instant-filter-eligible') ||
      this.supportsInstantFilters()
    ) {
      return Promise.resolve(null);
    }

    const indexUrl = this.targetUrl(form);
    const prefetchOptions = {
      routeKey: this.dataset.targetRouteKey,
      targetView: this.dataset.targetView,
      title: this.dataset.targetTitle,
      allowNavigationChange: true,
      allowSameRoute: true,
      collectionTemplate,
    };
    let preloadPromise;
    if (
      priority === 'high' &&
      typeof this.collection?.prioritizePrefetchUrl === 'function'
    ) {
      preloadPromise = this.collection.prioritizePrefetchUrl(
        indexUrl.href,
        prefetchOptions
      );
    } else {
      preloadPromise = this.collection?.prefetchUrl(indexUrl.href, {
        ...prefetchOptions,
        priority,
      });
    }

    return Promise.resolve(preloadPromise).then((data) => {
      const sourceForm = data?.filterOwner?.querySelector(
        '[data-collection-ref="filter-form"]'
      );
      const previewIndex = this.instantIndex(data?.results);
      if (previewIndex) this.previewCards = previewIndex.cards;
      if (sourceForm) this.syncOptionAvailability(sourceForm);
      return data;
    });
  }

  queueInstantBootstrap(
    form,
    restoreFocus,
    historyMode = 'push',
    landOnResults = false,
    announceResults = true
  ) {
    const desiredUrl = this.filterUrl(form);
    if (this.instantBootstrap) {
      this.instantBootstrap.desiredUrl = desiredUrl;
      this.instantBootstrap.restoreFocus = restoreFocus;
      this.instantBootstrap.announceResults = announceResults;
      this.stageInstantBootstrapUrl(this.instantBootstrap);
      this.updateProvisionalOptionAvailability(form);
      if (this.instantBootstrap.data) {
        this.prepareInstantBootstrap(this.instantBootstrap.data, this.instantBootstrap);
      }
      return;
    }

    const transaction = {
      announceResults,
      data: null,
      desiredUrl,
      historyMode,
      landOnResults,
      originalState: history.state,
      originalUrl: window.location.href,
      preparedData: null,
      restoreFocus,
    };
    this.instantBootstrap = transaction;
    this.stageInstantBootstrapUrl(transaction);
    this.updateProvisionalOptionAvailability(form);
    this.bootstrapInstantFilters(form, transaction).finally(() => {
      if (this.instantBootstrap === transaction) this.instantBootstrap = null;
    });
  }

  cancelInstantBootstrap({ cancelNavigation = false } = {}) {
    this.restoreInstantBootstrapUrl(this.instantBootstrap);
    this.instantBootstrap = null;
    if (cancelNavigation) this.collection?.cancelActiveNavigation?.();
  }

  stageInstantBootstrapUrl(transaction) {
    if (!transaction?.desiredUrl) return;

    const destination = new URL(transaction.desiredUrl, window.location.href);
    if (destination.origin !== window.location.origin) return;

    const path = `${destination.pathname}${destination.search}${destination.hash}`;
    history.replaceState(
      {
        ...(history.state || {}),
        dieuxCollectionUrl: path,
      },
      '',
      path
    );
    transaction.historyStaged = true;
  }

  restoreInstantBootstrapUrl(transaction) {
    if (!transaction?.historyStaged) return;

    history.replaceState(
      transaction.originalState,
      '',
      transaction.originalUrl
    );
    transaction.historyStaged = false;
  }

  updateProvisionalOptionAvailability(form) {
    const instantIndex = this.instantIndex(this.results);
    if (!instantIndex) return;

    this.instantCards = instantIndex.cards;
    this.updateOptionAvailability(form, this.selectedGroups(form));
  }

  resetOptionAvailability(form) {
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.disabled = false;
    });
  }

  async bootstrapInstantFilters(form, transaction) {
    const bootstrapUrl = this.targetUrl(form);

    await this.navigate(bootstrapUrl.href, {
      fallbackUrl: () => transaction.desiredUrl || this.filterOrigin().href,
      historyMode: transaction.historyMode || 'push',
      keepOpen: true,
      landOnResults: transaction.landOnResults,
      restoreFocus: transaction.restoreFocus,
      announceResults: transaction.announceResults,
      requirePreparedCommit: true,
      prepareData: (data) => {
        transaction.data = data;
        return this.prepareInstantBootstrap(data, transaction);
      },
    });
  }

  prepareInstantBootstrap(data, transaction) {
    if (
      this.instantBootstrap !== transaction ||
      !transaction.desiredUrl
    ) {
      return null;
    }

    const desiredUrl = transaction.desiredUrl;
    const form = data.filterOwner?.querySelector('[data-collection-ref="filter-form"]');
    const instantIndex = this.instantIndex(data.results);
    if (!desiredUrl || !form || !instantIndex) return null;

    this.syncFormFromUrl(form, desiredUrl);
    this.instantCards = instantIndex.cards;
    const selectedGroups = this.selectedGroups(form);
    const matchingCards = instantIndex.cards.filter((card) =>
      this.cardMatches(card, selectedGroups)
    );
    this.updateInstantResults(instantIndex, matchingCards);

    this.updateOptionAvailability(form, selectedGroups);
    this.syncOptionAvailability(form);
    this.updateFilterCounts(selectedGroups, {
      root: data.filterOwner,
      trigger: data.filterControl?.querySelector('[data-collection-filter]'),
      clearButton: data.filterControl?.querySelector('[data-collection-filter-clear]'),
    });
    this.syncFilteredHeaderState(this.filterCount(selectedGroups), {
      header: data.header,
      identity: data.identity,
    });

    transaction.preparedData ||= {
      beforeCommit: () => this.restoreInstantBootstrapUrl(transaction),
      afterCommit: () => this.finalizeInstantBootstrap(transaction),
      afterTransition: () => {
        const { count, filterCount } = transaction.preparedData;
        if (transaction.announceResults) this.announceResults(count, filterCount);
      },
    };
    transaction.preparedData.commitUrl = desiredUrl;
    transaction.preparedData.count = matchingCards.length;
    transaction.preparedData.filterCount = this.filterCount(selectedGroups);
    transaction.preparedData.restoreFocus = transaction.restoreFocus;
    return transaction.preparedData;
  }

  finalizeInstantBootstrap(transaction) {
    if (this.instantBootstrap !== transaction) return;

    const results = this.collection?.querySelector('[data-collection-ref="results"]');
    const form = this.querySelector('[data-collection-ref="filter-form"]');
    const instantIndex = this.instantIndex(results);
    if (!results || !form || !instantIndex) return;

    this.results = results;
    this.instantGrid = instantIndex.grid;
    this.instantEmptyState = instantIndex.emptyState;
    this.instantCards = instantIndex.cards;
    this.markCommittedFromUrl(transaction.desiredUrl);
    if (!this.dialog?.open) this.syncFormFromUrl(form, transaction.desiredUrl);
    this.updateDraftState(form);
    this.updateCommittedCounts();
    this.syncCardMembers(
      this.instantCards.filter((card) => !card.element.hidden),
      this.selectedGroups(form)
    );
  }

  instantIndex(results) {
    const grid = results?.querySelector('[data-collection-grid]');
    if (!results?.hasAttribute('data-instant-filter-index') || !grid) return null;

    const filterValues = (source) => new Map(
      Array.from(filterAttributeByParam.entries()).map(([paramName, datasetKey]) => [
        paramName,
        new Set((source.dataset[datasetKey] || '').split(/\s+/).filter(Boolean)),
      ])
    );
    const cards = Array.from(grid.querySelectorAll('[data-collection-card]')).map((element) => ({
      element,
      defaultProductId: element.dataset.productId,
      values: filterValues(element),
      members: Array.from(
        element.querySelectorAll(':scope > template[data-collection-card-member]'),
        (member) => ({
          productId: member.dataset.productId,
          listed: !member.hasAttribute('data-collection-card-member-unlisted'),
          values: filterValues(member),
        })
      ),
    }));

    return {
      grid,
      cards,
      emptyState: results.querySelector('[data-collection-instant-empty]'),
    };
  }

  refreshInstantIndex() {
    const results = this.collection?.querySelector('[data-collection-ref="results"]');
    const grid = results?.querySelector('[data-collection-grid]');
    if (
      results?.hasAttribute('data-instant-filter-index') &&
      grid &&
      grid === this.instantGrid &&
      this.instantCards?.length
    ) {
      this.results = results;
      this.instantEmptyState = results.querySelector('[data-collection-instant-empty]');
      return true;
    }

    const instantIndex = this.instantIndex(results);
    this.results = results;

    if (!instantIndex) {
      this.instantGrid = null;
      this.instantEmptyState = null;
      this.instantCards = [];
      return false;
    }

    this.instantGrid = instantIndex.grid;
    this.instantEmptyState = instantIndex.emptyState;
    this.instantCards = instantIndex.cards;
    return true;
  }

  supportsInstantFilters() {
    return (
      this.dataset.filterBehavior === 'instant' &&
      this.collection?.hasAttribute('data-filter-target-self') &&
      this.refreshInstantIndex()
    );
  }

  selectedGroups(form) {
    const groups = new Map();
    form.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
      if (!filterAttributeByParam.has(input.name)) return;
      if (!groups.has(input.name)) {
        groups.set(input.name, {
          operator: normalizedFilterOperator(
            input.closest('[data-filter-operator]')?.dataset.filterOperator
          ),
          values: new Set(),
        });
      }
      groups.get(input.name).values.add(input.value);
    });
    return groups;
  }

  cardMatches(card, groups) {
    return filterGroupsMatchCard(card, groups);
  }

  syncCardMembers(cards, groups) {
    cards.forEach((card) => {
      if ((card.members?.length || 0) < 2) return;
      const productCard = card.element.querySelector('product-card');
      if (typeof productCard?.selectState !== 'function') return;

      const cardOptions = Array.from(productCard.querySelectorAll('[data-product-card-option]'));
      const memberOptions = card.members.map(
        (member, index) =>
          cardOptions.find((option) => option.dataset.productId === member.productId) ||
          (cardOptions.length === card.members.length ? cardOptions[index] : null)
      );
      if (memberOptions.some((option) => !option)) return;

      const currentIndex = memberOptions.findIndex(
        (option) => option.dataset.productId === productCard.dataset.productId
      );
      const targetIndex = preferredCardMember(card, groups, {
        currentIndex,
        autoSelected: card.element.hasAttribute('data-filter-member-selected'),
      });
      if (targetIndex < 0) return;

      if (targetIndex !== currentIndex) {
        const option = memberOptions[targetIndex];
        productCard.selectState(option.dataset.productCardStateKey, option, { announce: false });
      }
      card.element.toggleAttribute(
        'data-filter-member-selected',
        card.members[targetIndex].productId !== card.defaultProductId
      );
    });
  }

  updateInstantResults(instantIndex, matchingCards) {
    const matchingElements = new Set(matchingCards.map((card) => card.element));

    instantIndex.cards.forEach((card) => {
      card.element.hidden = !matchingElements.has(card.element);
    });
    instantIndex.grid.hidden = matchingCards.length === 0;
    if (instantIndex.emptyState) {
      instantIndex.emptyState.hidden = matchingCards.length > 0;
    }
  }

  updateOptionAvailability(form, selectedGroups, cards = this.instantCards) {
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (input.checked) {
        input.disabled = false;
        return;
      }

      const candidateGroups = withCandidateFilterValue(
        selectedGroups,
        input.name,
        input.value,
        input.closest('[data-filter-operator]')?.dataset.filterOperator
      );
      input.disabled = !cards.some((card) => this.cardMatches(card, candidateGroups));
    });
  }

  syncOptionAvailability(sourceForm) {
    const currentForm = this.querySelector('[data-collection-ref="filter-form"]');
    if (!currentForm || currentForm === sourceForm) return;

    const currentGroupsContainer = currentForm.querySelector(
      '[data-collection-filter-groups]'
    );
    const currentGroups = new Map(
      Array.from(currentForm.querySelectorAll('[data-filter-param]')).map((group) => [
        group.dataset.filterParam,
        group,
      ])
    );
    if (currentGroupsContainer) {
      sourceForm.querySelectorAll('[data-filter-param]').forEach((sourceGroup) => {
        const currentGroup = currentGroups.get(sourceGroup.dataset.filterParam);
        if (!currentGroup) return;

        currentGroupsContainer.append(currentGroup);
        const currentValues = currentGroup.querySelector('[data-filter-values]');
        if (!currentValues) return;

        const currentChoices = new Map(
          Array.from(currentGroup.querySelectorAll('[data-filter-choice]')).flatMap((choice) => {
            const input = choice.querySelector('input[type="checkbox"]');
            return input ? [[filterInputKey(input), choice]] : [];
          })
        );
        const sourceKeys = Array.from(
          sourceGroup.querySelectorAll('input[type="checkbox"]')
        ).map(filterInputKey);
        const sourceKeySet = new Set(sourceKeys);

        orderedFilterValueKeys(Array.from(currentChoices.keys()), sourceKeys).forEach((key) => {
          const choice = currentChoices.get(key);
          choice.hidden = !sourceKeySet.has(key);
          currentValues.append(choice);
        });
      });
    }

    const sourceOperators = new Map(
      Array.from(sourceForm.querySelectorAll('[data-filter-param]')).map((group) => [
        group.dataset.filterParam,
        normalizedFilterOperator(group.dataset.filterOperator),
      ])
    );
    currentForm.querySelectorAll('[data-filter-param]').forEach((group) => {
      const sourceOperator = sourceOperators.get(group.dataset.filterParam);
      if (sourceOperator) group.dataset.filterOperator = sourceOperator;
    });

    const sourceInputs = new Map(
      Array.from(sourceForm.querySelectorAll('input[type="checkbox"]')).map((input) => [
        filterInputKey(input),
        input,
      ])
    );

    currentForm.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const sourceInput = sourceInputs.get(filterInputKey(input));
      input.disabled = !sourceInput || sourceInput.disabled;
    });

    if (this.dialog?.open) this.updateDraftState(currentForm);
  }

  syncFormFromUrl(form, url) {
    const destination = new URL(url, window.location.href);
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.checked = destination.searchParams.getAll(input.name).includes(input.value);
      input.disabled = false;
    });
  }

  onCollectionCommit() {
    this.refreshTrigger();
    this.markCommitted();
    this.refreshFilterCounts();
    if (!this.instantBootstrap) this.refreshInstantIndex();
  }

  applyInstantUrl(url, { historyMode = 'none', restoreFocus = false } = {}) {
    if (!this.supportsInstantFilters()) return false;

    const destination = new URL(url, window.location.href);
    const form = this.querySelector('[data-collection-ref="filter-form"]');
    const hasActiveFilters = Array.from(destination.searchParams.entries()).some(
      ([key, value]) => filterAttributeByParam.has(key) && value
    );
    const renderedPath = this.filtersInPlace
      ? new URL(this.collection?.lastCommittedPageUrl || window.location.href).pathname
      : form && this.committedUrl(form).pathname;
    if (
      !form ||
      (!hasActiveFilters && !this.filtersInPlace) ||
      destination.origin !== window.location.origin ||
      destination.pathname !== renderedPath
    ) {
      return false;
    }

    this.syncFormFromUrl(form, destination);
    this.markCommitted(form);
    this.applyInstantFilters(form, {
      url: destination.href,
      historyMode,
      restoreFocus,
    });
    return true;
  }

  applyInstantFilters(
    form,
    {
      url = this.filterUrl(form),
      historyMode = 'push',
      previousPageUrl,
      restoreFocus = false,
      landOnResults = false,
      announceResults = true,
    } = {}
  ) {
    if (!this.supportsInstantFilters()) return false;

    const selectedGroups = this.selectedGroups(form);
    const matchingCards = this.instantCards.filter((card) =>
      this.cardMatches(card, selectedGroups)
    );
    this.updateOptionAvailability(form, selectedGroups);
    this.updateFilterCounts(selectedGroups);
    this.syncFilteredHeaderState(this.filterCount(selectedGroups));
    this.restoreFilterFocus(restoreFocus);

    const commit = () => {
      this.updateInstantResults(
        {
          grid: this.instantGrid,
          cards: this.instantCards,
          emptyState: this.instantEmptyState,
        },
        matchingCards
      );
      this.syncCardMembers(matchingCards, selectedGroups);

      this.collection.commitInstantFilterUrl?.(url, {
        historyMode,
        previousPageUrl,
      });
      if (announceResults) {
        this.announceResults(matchingCards.length, this.filterCount(selectedGroups));
      }
    };

    if (typeof this.collection.transitionInstantResults === 'function') {
      this.collection.transitionInstantResults(commit, { glideToResults: landOnResults });
    } else {
      commit();
      if (landOnResults) this.collection.landOnResults?.();
    }
    return true;
  }

  restoreFilterFocus(restoreFocus) {
    if (restoreFocus?.type === 'filter-option') {
      Array.from(
        this.querySelectorAll(
          '[data-collection-ref="filter-form"] input[type="checkbox"]'
        )
      )
        .find(
          (input) =>
            input.name === restoreFocus.name &&
            input.value === restoreFocus.value &&
            !input.disabled
        )
        ?.focus({ preventScroll: true });
    } else if (restoreFocus === 'filter') {
      this.trigger?.focus({ preventScroll: true });
    }
  }

  /** @returns {Record<string, string | Array<{count: number, text: string}>>} */
  get messages() {
    if (!this.messageCache) {
      try {
        this.messageCache = JSON.parse(
          this.querySelector('[data-collection-filter-messages]')?.textContent || '{}'
        );
      } catch {
        this.messageCache = {};
      }
    }
    return this.messageCache;
  }

  /**
   * Resolves a locale message, choosing the plural form for `count` when given.
   * @param {string} key
   * @param {number} [count]
   * @returns {string}
   */
  message(key, count) {
    const entry = this.messages[key];
    if (typeof entry === 'string') return entry;
    if (!Array.isArray(entry) || !entry.length || count === undefined) return '';
    return formatItemCount(count, entry, document.documentElement.lang || 'en');
  }

  /**
   * Announces the drawer's draft: how many values are selected and, when the target's
   * cards are indexed, how many products that selection would show.
   * @param {Map<string, {operator: string, values: Set<string>}> | null} selectedGroups
   */
  announceDraft(selectedGroups) {
    if (!selectedGroups) return;

    const selected = this.filterCount(selectedGroups);
    const cards = this.availabilityCards();
    const parts = [
      selected ? this.message('selected', selected) : this.message('none_selected'),
    ];
    if (cards.length) {
      const matching = cards.filter((card) => this.cardMatches(card, selectedGroups)).length;
      parts.push(this.message('matching', matching));
    }
    announce(
      this.querySelector('[data-collection-filter-status]'),
      parts.filter(Boolean).join(' ')
    );
  }

  /**
   * Announces a committed filter change from the collection's status region.
   * @param {number} resultCount
   * @param {number} filterCount
   */
  announceResults(resultCount, filterCount) {
    const parts = [
      filterCount ? this.message('applied', filterCount) : this.message('cleared'),
      resultCount ? this.message('results', resultCount) : this.message('no_results'),
    ];
    announce(this.collection?.status, parts.filter(Boolean).join(' '));
  }

  /** @param {number} filterCount */
  announceVisibleResults(filterCount) {
    const resultCount =
      this.collection?.querySelectorAll(
        '[data-collection-ref="results"] [data-collection-card]:not([hidden])'
      ).length || 0;
    this.announceResults(resultCount, filterCount);
  }

  filterOrigin() {
    const currentUrl = new URL(window.location.href);
    const requestedOrigin = currentUrl.searchParams.get(filterOriginParam);
    const directoryLink = this.collection?.links.find(
      (link) => link.dataset.collectionTargetView === 'list-collections'
    );
    const defaultOriginPath = directoryLink
      ? new URL(directoryLink.href, currentUrl).pathname
      : defaultFilterOrigin;
    const fallbackPath =
      currentUrl.pathname === new URL(this.querySelector('form')?.action || '', currentUrl).pathname
        ? defaultOriginPath
        : currentUrl.pathname;
    const origin = new URL(requestedOrigin || fallbackPath, window.location.origin);
    const isValidOrigin = this.collection?.isValidFilterOrigin
      ? this.collection.isValidFilterOrigin(origin)
      : origin.origin === window.location.origin &&
        (
          origin.pathname === defaultFilterOrigin ||
          /^\/collections\/[^/]+\/?$/.test(origin.pathname)
        );

    return isValidOrigin
      ? origin
      : new URL(defaultOriginPath, window.location.origin);
  }

  filterOriginRoute(origin) {
    return this.collection.routeForUrl(origin);
  }

  isAtFilterOrigin() {
    const current = new URL(window.location.href);
    const origin = this.filterOrigin();
    const hasCommittedFilters = Array.from(current.searchParams.entries()).some(
      ([key, value]) => key.startsWith('filter.') && value
    );

    return (
      !hasCommittedFilters &&
      current.origin === origin.origin &&
      current.pathname === origin.pathname
    );
  }

  async navigate(
    url,
    {
      keepOpen = false,
      restoreFocus = false,
      fallbackUrl = url,
      routeKey = this.dataset.targetRouteKey,
      targetView = this.dataset.targetView,
      title = this.dataset.targetTitle,
      historyMode = 'push',
      announceResults = true,
      prepareData = (data) => {
        const sourceForm = data.filterOwner?.querySelector(
          '[data-collection-ref="filter-form"]'
        );
        if (sourceForm) this.syncOptionAvailability(sourceForm);
        const form = this.querySelector('[data-collection-ref="filter-form"]');
        const filterCount = form ? this.filterCount(this.selectedGroups(form)) : 0;
        return {
          afterTransition: () => {
            if (announceResults) this.announceVisibleResults(filterCount);
          },
        };
      },
      requirePreparedCommit = false,
      landOnResults = false,
    } = {}
  ) {
    if (!this.collection) {
      window.location.assign(url);
      return;
    }

    const apply = this.querySelector('[data-collection-filter-apply]');
    const form = this.querySelector('[data-collection-ref="filter-form"]');
    apply?.setAttribute('aria-busy', 'true');
    form?.setAttribute('aria-busy', 'true');
    if (!keepOpen) this.close({ restoreFocus: false });

    try {
      return await this.collection.navigateToUrl(url, {
        fallbackUrl,
        routeKey,
        targetView,
        title,
        historyMode,
        restoreFocus: keepOpen ? restoreFocus : 'filter',
        preserveFilterDialog: keepOpen,
        allowNavigationChange: true,
        prepareData,
        requirePreparedCommit,
        resultsOnly: this.filtersInPlace,
        glideToResults: landOnResults,
        silent: !announceResults,
      });
    } finally {
      apply?.removeAttribute('aria-busy');
      form?.removeAttribute('aria-busy');
    }
  }

  refreshFilterCounts() {
    const form = this.querySelector('[data-collection-ref="filter-form"]');
    if (!form) return;
    this.updateFilterCounts(this.selectedGroups(form));
  }

  filterCount(selectedGroups) {
    return Array.from(selectedGroups.values()).reduce(
      (total, group) => total + group.values.size,
      0
    );
  }

  syncFilteredHeaderState(
    count,
    {
      header = this.collection?.querySelector('.g-collection__header'),
      identity = this.collection?.querySelector('[data-collection-ref="identity"]'),
    } = {}
  ) {
    if (this.filtersInPlace) return;

    const titles = new Set(
      [
        header?.querySelector('.g-collection__title'),
        identity?.querySelector('.g-collection__title'),
      ].filter(Boolean)
    );
    if (!titles.size) return;

    const isFiltered = count > 0;
    header?.classList.toggle('g-collection__header--filtered', isFiltered);
    titles.forEach((title) => title.classList.toggle('visually-hidden', isFiltered));
  }

  updateDraftCounts(selectedGroups, { root = this } = {}) {
    const title = root.querySelector('[data-collection-filter-title]');
    const reset = root.querySelector('[data-collection-filter-reset]');
    const count = this.filterCount(selectedGroups);
    if (title) {
      const label =
        title.dataset.filterTitleLabel ||
        title.textContent.trim().replace(/\s+\(\d+\)$/, '');
      title.textContent = count > 0 ? `${label} (${count})` : label;
    }
    if (reset) {
      reset.hidden = count === 0;
    }
  }

  updateCommittedCounts({ trigger = this.trigger, clearButton = this.clearButton } = {}) {
    const count = this.committedSelection?.size || 0;
    const triggerLabel =
      trigger?.dataset.collectionFilterLabel ||
      trigger?.textContent.trim().replace(/\s+\(\d+\)$/, '');
    if (trigger && triggerLabel) {
      trigger.textContent = `${triggerLabel} (${count})`;
    }
    if (clearButton) {
      clearButton.hidden = count === 0;
    }
  }

  updateFilterCounts(
    selectedGroups,
    {
      root = this,
      trigger = this.trigger,
      clearButton = this.clearButton,
    } = {}
  ) {
    this.updateDraftCounts(selectedGroups, { root });
    this.updateCommittedCounts({ trigger, clearButton });
  }
}

if (!customElements.get('collection-filter-drawer')) {
  customElements.define('collection-filter-drawer', CollectionFilterDrawer);
}
